import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Modal } from 'react-bootstrap';
import { IconButton, Typography, Box } from '@mui/material';
import { BiArrowBack } from 'react-icons/bi';
import DataSourceChart from './DataSourceChart';
import DashGraphConfigService from '../../../core/service/DashGraphConfigService';
import { SANKEY_NODE_TYPE } from '../../../core/constants/common';
import Utils from '../../../core/utilities/Utils';
import './DataSourceTreeModal.scss';

/**
 * Collect unique metric and datapoint refIds from a sankey config tree for API filtering.
 */
function collectIdsFromSankeyTree(nodes) {
	const metricIds = [];
	const metricDataPointIds = [];
	const seenM = new Set();
	const seenDp = new Set();

	function visit(node) {
		if (!node || typeof node !== 'object') return;
		const rid = node.refId;
		if (rid == null) return;
		if (node.type === SANKEY_NODE_TYPE.METRIC) {
			if (!seenM.has(rid)) {
				seenM.add(rid);
				metricIds.push(rid);
			}
		} else if (node.type === SANKEY_NODE_TYPE.DP) {
			if (!seenDp.has(rid)) {
				seenDp.add(rid);
				metricDataPointIds.push(rid);
			}
		}
		(node.children || []).forEach(visit);
	}

	(Array.isArray(nodes) ? nodes : []).forEach(visit);
	return { metricIds, metricDataPointIds };
}

function normalizeSankeyProp(sankeyTree) {
	if (sankeyTree == null) return [];
	if (typeof sankeyTree === 'string') {
		try {
			const parsed = JSON.parse(sankeyTree);
			return Array.isArray(parsed) ? parsed : [];
		} catch {
			return [];
		}
	}
	return Array.isArray(sankeyTree) ? sankeyTree : [];
}

// Helper for layout
const NODE_WIDTH = 250;
const BASE_NODE_HEIGHT = 90;
const VERTICAL_GAP = 30;
const HORIZONTAL_GAP = 80;
const MIN_NODE_HEIGHT = 60;
const MAX_NODE_HEIGHT = 200;
const TEXT_LINE_HEIGHT = 24;
const PADDING = 10;

// Function to calculate required height based on text content
function calculateNodeHeight(text, value) {
  // Handle edge cases
  if (!text || !value) {
    return BASE_NODE_HEIGHT;
  }
  
  try {
    // Calculate text width to determine if it wraps
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = '1rem Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    
    // Measure text width
    const textWidth = context.measureText(text).width;
    const valueWidth = context.measureText(value).width;
    
    // Calculate how many lines the text will take
    const availableWidth = NODE_WIDTH - (PADDING * 2);
    const textLines = Math.ceil(textWidth / availableWidth);
    const valueLines = Math.ceil(valueWidth / availableWidth);
    
    // Calculate required height
    const requiredHeight = Math.max(
      MIN_NODE_HEIGHT,
      Math.min(
        MAX_NODE_HEIGHT,
        BASE_NODE_HEIGHT + (Math.max(textLines, valueLines) - 1) * TEXT_LINE_HEIGHT
      )
    );
    
    return requiredHeight;
  } catch (error) {
    // Fallback to base height if canvas measurement fails
    console.warn('Failed to calculate text height, using base height:', error);
    return BASE_NODE_HEIGHT;
  }
}

function getTreeLayout(data) {
  // Returns: { nodes: [{x, y, data, level, idx}], links: [{from: idx, to: idx}] }
  // Simple horizontal mindmap: root at (0,0), children to the right, stacked vertically
  const nodes = [];
  const links = [];
  let y = 0;
  let idx = 0;

  function walk(node, level, parentIdx = null) {
    const thisIdx = idx++;
    const nodeY = y;
    
    // Calculate height for this node
    const nodeHeight = calculateNodeHeight(node.name, node.value);
    
    nodes.push({ 
      ...node, 
      x: level * (NODE_WIDTH + HORIZONTAL_GAP), 
      y: nodeY, 
      level, 
      idx: thisIdx,
      height: nodeHeight
    });
    
    if (parentIdx !== null) {
      links.push({ from: parentIdx, to: thisIdx });
    }
    
    if (node.children && node.children.length > 0) {
      let startY = y;
      node.children.forEach((child, i) => {
        walk(child, level + 1, thisIdx);
        if (i < node.children.length - 1) {
          // Use the height of the current child for spacing
          const childHeight = calculateNodeHeight(child.name, child.value);
          y += childHeight + VERTICAL_GAP;
        }
      });
      // Center parent if children are stacked
      const endY = y;
      const parentNode = nodes[thisIdx];
      parentNode.y = (startY + endY) / 2;
    }
  }
  walk(data, 0);
  return { nodes, links };
}

const DataSourceTreeModal = ({ t, open, onClose, onBack, graphId, graphName, standardId, sankeyTree, periodFrom, periodTo, businessUnitIds }) => {
  const [chartModal, setChartModal] = useState({ open: false, name: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [graphData, setGraphData] = useState(null);

  const sankeyConfigNodes = useMemo(() => normalizeSankeyProp(sankeyTree), [sankeyTree]);

  const { metricIds, metricDataPointIds } = useMemo(
    () => collectIdsFromSankeyTree(sankeyConfigNodes),
    [sankeyConfigNodes]
  );

  const metricIdsParam = metricIds.join(',');
  const metricDpIdsParam = metricDataPointIds.join(',');

  useEffect(() => {
    const fetchGraphData = async () => {
      if (!open || !standardId) return;

      try {
        setLoading(true);
        setError(null);
        const response = await DashGraphConfigService.getGraphWithMetrics(
          standardId,
          periodFrom,
          periodTo,
          businessUnitIds,
          metricIdsParam,
          metricDpIdsParam
        );
        setGraphData(response.data);
      } catch (err) {
        console.error('Error fetching graph data:', err);
        setError(err.message || 'Failed to fetch graph data');
      } finally {
        setLoading(false);
      }
    };

    fetchGraphData();
  }, [open, standardId, periodFrom, periodTo, businessUnitIds, metricIdsParam, metricDpIdsParam]);

  if (!open && !chartModal.open) return null;
  if (chartModal.open) {
    return (
      <DataSourceChart
        open={true}
        onClose={() => setChartModal({ open: false, name: '' })}
        onBack={() => setChartModal({ open: false, name: '' })}
        dataSourceName={chartModal.name}
      />
    );
  }

  /**
   * Map API payload + sankeyTree config into the mindmap tree shape expected by getTreeLayout.
   */
  const transformDataToTree = (apiData, title, configNodes) => {
    if (!apiData) return null;

    const raw = apiData.data || apiData;
    const metrics = raw.metrics || [];
    const metricDataPoints = raw.metricDataPoints || [];
    const metricById = new Map(metrics.map((m) => [m.id, m]));
    const dpById = new Map(metricDataPoints.map((dp) => [dp.id, dp]));

    const formatMetric = (m) =>
      Utils.formatValueStringWithThousandSeparators(
        m.value ?? m.totalValue ?? 0,
        m.unitOfMeasure ?? m.totalUnitOfMeasure ?? ''
      );
    const formatDp = (dp) =>
      Utils.formatValueStringWithThousandSeparators(
        dp.combined ?? dp.value ?? 0,
        dp.unit ?? dp.unitOfMeasure ?? ''
      );

    const configNodeToDisplay = (node) => {
      if (!node || typeof node !== 'object') return null;
      if (node.type === SANKEY_NODE_TYPE.METRIC) {
        const m = metricById.get(node.refId);
        const name = m?.name || `Metric ${node.refId}`;
        const value = m ? formatMetric(m) : '—';
        const children = (node.children || []).map(configNodeToDisplay).filter(Boolean);
        return { name, value, children };
      }
      if (node.type === SANKEY_NODE_TYPE.DP) {
        const dp = dpById.get(node.refId);
        const name = dp?.name || `Data point ${node.refId}`;
        const value = dp ? formatDp(dp) : '—';
        const children = (node.children || []).map(configNodeToDisplay).filter(Boolean);
        return { name, value, children };
      }
      return null;
    };

    let totalValue = 0;
    let totalUnit = '';
    if (metrics.length > 0) {
      totalValue = metrics.reduce((sum, m) => sum + (m.value ?? m.totalValue ?? 0), 0);
      totalUnit = metrics[0]?.unitOfMeasure ?? metrics[0]?.totalUnitOfMeasure ?? '';
    } else if (metricDataPoints.length > 0) {
      totalValue = metricDataPoints.reduce((sum, dp) => sum + (dp.combined ?? dp.value ?? 0), 0);
      totalUnit = metricDataPoints[0]?.unit ?? '';
    }

    const children = (configNodes || []).map(configNodeToDisplay).filter(Boolean);

    return {
      name: title || 'Graph Data',
      value: Utils.formatValueStringWithThousandSeparators(totalValue, totalUnit),
      children
    };
  };

  const treeData = transformDataToTree(graphData, graphName, sankeyConfigNodes);

  if (loading) {
    return (
      <Modal show={open} onHide={onClose} size="xl" centered className="data-source-tree-modal NewDashboard_modal sankeyDiagramModal01">
        <Modal.Header closeButton>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <IconButton
                onClick={onBack}
                className="back-button"
                size="small"
                title={t('DataSourceTreeModal.back.label')}
              >
                <BiArrowBack />
              </IconButton>
              <Typography variant="h6" className="modal-title-text">
                {t('ChartMaximizeModal.sankeyDiagram.label')}
              </Typography>
            </Box>
          </Box>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('DataSourceTreeModal.loading.label')}</span>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal show={open} onHide={onClose} size="xl" centered className="data-source-tree-modal NewDashboard_modal sankeyDiagramModal02">
        <Modal.Header closeButton>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <IconButton
                onClick={onBack}
                className="back-button"
                size="small"
                title={t('DataSourceTreeModal.back.label')}
              >
                <BiArrowBack />
              </IconButton>
              <Typography variant="h6" className="modal-title-text">
                {t('ChartMaximizeModal.sankeyDiagram.label')}
              </Typography>
            </Box>
          </Box>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
            <div className="text-center">
              <div className="text-danger mb-2">{t('DataSourceTreeModal.error.label')}</div>
              <div className="text-muted">{error}</div>
              <button 
                className="btn btn-primary mt-3"
                onClick={() => window.location.reload()}
              >
                {t('DataSourceTreeModal.retry.label')}
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  if (!treeData) return null;

  // Layout
  const { nodes, links } = getTreeLayout(treeData);
  // Find bounds for SVG
  const minX = Math.min(...nodes.map(n => n.x));
  const minY = Math.min(...nodes.map(n => n.y));
  const maxX = Math.max(...nodes.map(n => n.x));
  const maxY = Math.max(...nodes.map(n => n.y));

  // Add buffer to height so last node is never cut off
  const svgWidth = maxX - minX + NODE_WIDTH + HORIZONTAL_GAP;
  const maxNodeHeight = Math.max(...nodes.map(n => n.height));
  const svgHeight = maxY - minY + maxNodeHeight + 40; // buffer at bottom
  const offsetX = -minX + 40;
  const offsetY = -minY + 40;

  return (
    <Modal
      show={open}
      onHide={onClose}
      size="xl"
      centered
      className="data-source-tree-modal NewDashboard_modal sankeyDiagramModal03"
     
    >
      <Modal.Header closeButton >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <IconButton
              onClick={onBack}
              className="back-button"
              size="small"
              title={t('DataSourceTreeModal.back.label')}
            >
              <BiArrowBack />
            </IconButton>
            <Typography variant="h6" className="modal-title-text">
              {t('ChartMaximizeModal.sankeyDiagram.label')}
            </Typography>
          </Box>
        </Box>
      </Modal.Header>
      <Modal.Body>
        <div className="tree-mindmap-wrapper" style={{ maxHeight: 'calc(100vh - 350px)', overflow: 'auto' }}>
          <svg
            width={svgWidth}
            height={svgHeight}
            style={{ display: 'block', margin: '0 auto', overflow: 'visible', width: '100%', maxWidth: '100%' }}
          >
            {/* Connectors */}
            {links.map((link, i) => {
              const from = nodes[link.from];
              const to = nodes[link.to];
              // Start at right center of parent, end at left center of child
              const startX = from.x + NODE_WIDTH + offsetX;
              const startY = from.y + from.height / 2 + offsetY;
              const endX = to.x + offsetX;
              const endY = to.y + to.height / 2 + offsetY;
              // Curved path (cubic Bezier)
              const c1 = startX + 30;
              const c2 = endX - 30;
              return (
                <path
                  key={i}
                  d={`M${startX},${startY} C${c1},${startY} ${c2},${endY} ${endX},${endY}`}
                  stroke="#f59e42"
                  strokeWidth={3}
                  fill="none"
                  className="tree-mindmap-connector"
                />
              );
            })}
            {/* Nodes */}
            {nodes.map((node, i) => (
              <g
                key={i}
                className={
                  'tree-mindmap-node' +
                  (node.level === 0 ? ' tree-mindmap-node--root' : ' tree-mindmap-node--child')
                }
                style={{ cursor: node.level !== 0 ? 'pointer' : 'default' }}
                onClick={() => {
                  if (node.level !== 0) setChartModal({ open: true, name: node.name });
                }}
              >
                <rect
                  x={node.x + offsetX}
                  y={node.y + offsetY}
                  width={NODE_WIDTH}
                  height={node.height}
                  rx={16}
                  fill="#fff"
                  stroke={node.level === 0 ? '#22c55e' : '#f59e42'}
                  strokeWidth={3}
                  filter="url(#shadow)"
                />
                <foreignObject
                  x={node.x + offsetX}
                  y={node.y + offsetY}
                  width={NODE_WIDTH}
                  height={node.height}
                  style={{ pointerEvents: 'none' }}
                >
                  <div className="tree-mindmap-card" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: `${PADDING}px` }}>
                    <div className="tree-mindmap-label" style={{ color: '#334155', fontWeight: 500, fontSize: '1rem', textAlign: 'center', marginBottom: 4, wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                      {node.name}
                    </div>
                    <div className="tree-mindmap-value" style={{ color: node.level === 0 ? '#22c55e' : '#f59e42', fontWeight: 700, fontSize: '1.25rem', textAlign: 'center', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                      {node.value}
                    </div>
                  </div>
                </foreignObject>
              </g>
            ))}
            {/* Drop shadow filter */}
            <defs>
              <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#000" floodOpacity="0.10" />
              </filter>
            </defs>
          </svg>
        </div>
      </Modal.Body>
    </Modal>
  );
};

DataSourceTreeModal.propTypes = {
  t: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  graphId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  graphName: PropTypes.string,
  standardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  /** Dashboard graph sankey config: nodes with type METRIC | DP, refId, children */
  sankeyTree: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.string]),
  periodFrom: PropTypes.string,
  periodTo: PropTypes.string,
  businessUnitIds: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
};

export default withTranslation()(DataSourceTreeModal); 